import { useEffect, useRef, useState } from 'react'
import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import 'katex/dist/katex.min.css'
import type { ConversationDetail, ConversationMessage } from '../../api/agent'
import type { Tag } from '../../api/tags'
import ContextPills from './ContextPills'
import { MessageSources } from './ContextPills'
import AppendToNoteDialog from './AppendToNoteDialog'
import ChatInput from './ChatInput'

interface ChatPaneProps {
  conversation: ConversationDetail | null
  isLoadingConversation: boolean
  isSending: boolean
  optimisticUserMessage: string | null
  remainingTokens: number
  tokensRefreshTime: number | null
  inputValue: string
  onInputChange: (v: string) => void
  onSend: () => void
  onTokenLimitHit: () => void
  onConvertToNote: (messageId: string) => void
  onAppendToNote: (conversationId: string, messageId: string, targetNoteId: string) => Promise<void>
  onOpenContextBag?: () => void
  pinnedNoteCount: number
  activeTags: Tag[]
  /** Optional inline-editable title rendered in the title bar. */
  titleSlot?: React.ReactNode
  /** Total messages currently in this conversation (user + assistant). */
  messageCount: number
  /** True when the per-conversation hard limit has been reached. */
  atMessageLimit: boolean
}

/** Render message content — HTML for legacy, Markdown for new messages. */
function MessageContent({ message }: { message: ConversationMessage }) {
  if (message.content_format === 'html') {
    return (
      <div
        className="prose prose-sm max-w-none"
        dangerouslySetInnerHTML={{ __html: message.content }}
      />
    )
  }
  return (
    <div className="prose prose-sm max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={{
          // Open links in new tab
          a: ({ children, href, ...props }) => (
            <a href={href} {...props} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
          // Style inline code vs fenced code blocks
          code: ({ className, children, ...props }) => {
            const isBlock = !!className?.startsWith('language-')
            return isBlock ? (
              <code
                className={`${className ?? ''} block bg-gray-900 text-gray-100 rounded-lg p-3 text-xs overflow-x-auto`}
                {...props}
              >
                {children}
              </code>
            ) : (
              <code
                className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-xs font-mono"
                {...props}
              >
                {children}
              </code>
            )
          },
        }}
      >
        {message.content}
      </ReactMarkdown>
    </div>
  )
}

function AssistantBubble({
  message,
  conversationId,
  onConvertToNote,
  onAppendToNote,
}: {
  message: ConversationMessage
  conversationId: string
  onConvertToNote: (id: string) => void
  onAppendToNote: (convId: string, msgId: string, noteId: string) => Promise<void>
}) {
  const [copied, setCopied] = useState(false)
  const [showAppend, setShowAppend] = useState(false)

  async function handleCopy() {
    await navigator.clipboard.writeText(message.content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div data-testid="chat-message-assistant" className="flex items-start gap-3 max-w-3xl">
      {/* Avatar */}
      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      </div>

      <div className="flex-1 min-w-0">
        <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
          <MessageContent message={message} />
        </div>

        {/* Sources */}
        <MessageSources message={message} />

        {/* Action bar */}
        <div className="flex items-center gap-2 mt-2">
          <button
            data-testid="msg-convert-to-note"
            onClick={() => onConvertToNote(message.id)}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-gray-600 bg-white border border-gray-200 rounded-full hover:bg-gray-50 hover:border-gray-300 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Convert to Note
          </button>
          <button
            data-testid="msg-append-to-note"
            onClick={() => setShowAppend(true)}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-gray-600 bg-white border border-gray-200 rounded-full hover:bg-gray-50 hover:border-gray-300 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Append to Note
          </button>
          <button
            onClick={handleCopy}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-gray-600 bg-white border border-gray-200 rounded-full hover:bg-gray-50 hover:border-gray-300 transition-colors"
          >
            {copied ? (
              <>
                <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Copied
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy
              </>
            )}
          </button>
        </div>

        {showAppend && (
          <AppendToNoteDialog
            isOpen={showAppend}
            conversationId={conversationId}
            messageId={message.id}
            onConfirm={(noteId) => onAppendToNote(conversationId, message.id, noteId)}
            onClose={() => setShowAppend(false)}
          />
        )}
      </div>
    </div>
  )
}

function TypingIndicator() {
  return (
    <div className="flex items-start gap-3 max-w-3xl">
      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      </div>
      <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
        <div className="flex items-center gap-1 h-5">
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  )
}

export default function ChatPane({
  conversation,
  isLoadingConversation,
  isSending,
  optimisticUserMessage,
  remainingTokens,
  tokensRefreshTime,
  inputValue,
  onInputChange,
  onSend,
  onTokenLimitHit,
  onConvertToNote,
  onAppendToNote,
  onOpenContextBag,
  pinnedNoteCount,
  activeTags,
  titleSlot,
  messageCount,
  atMessageLimit,
}: ChatPaneProps) {
  const bottomRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [conversation?.messages, isSending, optimisticUserMessage])

  if (isLoadingConversation) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-400">
        <div className="w-8 h-8 border-3 border-gray-300 border-t-primary rounded-full animate-spin mr-3" />
        Loading conversation…
      </div>
    )
  }

  // ── Empty state (no conversation selected) — still render the input ────────
  if (!conversation) {
    return (
      <div className="flex flex-col h-full">
        {/* Empty state fills available space */}
        <div className="flex-1 flex flex-col items-center justify-center text-gray-400 px-8 text-center"
        data-testid="chat-empty-state"
      >
          <svg className="w-16 h-16 mb-4 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <p className="text-lg font-medium text-gray-500 mb-1">Research Lab</p>
          <p className="text-sm text-gray-400">
            Ask a question to start a new conversation, or select one from the history.
          </p>
        </div>
        {/* Input is always available to start a new conversation */}
        <ChatInput
          value={inputValue}
          onChange={onInputChange}
          onSend={onSend}
          isLoading={isSending}
          remainingTokens={remainingTokens}
          tokensRefreshTime={tokensRefreshTime}
          onTokenLimitHit={onTokenLimitHit}
          conversationAtLimit={false}
        />
      </div>
    )
  }

  const messages = conversation.messages ?? []

  // Keep these in sync with AgentConfig on the backend
  const MAX_CONVERSATION_MESSAGES = 20
  const WARNING_THRESHOLD = 16 // warn when 2 turns remain
  const turnsRemaining = Math.max(0, Math.floor((MAX_CONVERSATION_MESSAGES - messageCount) / 2))
  const showApproachingWarning = !atMessageLimit && messageCount >= WARNING_THRESHOLD
  const effectiveAtLimit = atMessageLimit || messageCount >= MAX_CONVERSATION_MESSAGES

  return (
    <div className="flex flex-col h-full">
      {/* Conversation title bar */}
      <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0 flex items-center gap-2">
        {titleSlot ?? (
          <h2 className="text-sm font-semibold text-gray-900 truncate">{conversation.title}</h2>
        )}
      </div>

      {/* Context pills */}
      <ContextPills
        mode={conversation.mode}
        tags={activeTags}
        pinnedNoteCount={pinnedNoteCount}
        onOpenContextBag={onOpenContextBag}
      />

      {/* Conversation length warning / limit banners */}
      {effectiveAtLimit && (
        <div data-testid="conv-at-limit-banner" className="flex items-center gap-2 px-4 py-2 bg-red-50 border-b border-red-200 flex-shrink-0">
          <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-xs text-red-700 flex-1">
            This conversation has reached the maximum of{' '}
            <span className="font-semibold">{MAX_CONVERSATION_MESSAGES / 2} turns</span>.
            Please{' '}
            <button className="underline font-medium hover:text-red-900" onClick={() => window.dispatchEvent(new CustomEvent('assistant:new-chat'))}>
              start a new conversation
            </button>
            {' '}to continue.
          </p>
        </div>
      )}
      {showApproachingWarning && (
        <div data-testid="conv-approaching-limit-banner" className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200 flex-shrink-0">
          <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-xs text-amber-800">
            <span className="font-semibold">{turnsRemaining} turn{turnsRemaining !== 1 ? 's' : ''} remaining</span>
            {' '}in this conversation. Consider starting a new one soon.
          </p>
        </div>
      )}

      {/* Messages */}
      <div data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.map((msg) =>
          msg.role === 'user' ? (
            <div key={msg.id} data-testid="chat-message-user" className="flex justify-end">
              <div className="max-w-xl bg-primary text-white rounded-2xl rounded-tr-sm px-4 py-3 shadow-sm">
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ) : (
            <AssistantBubble
              key={msg.id}
              message={msg}
              conversationId={conversation.id}
              onConvertToNote={onConvertToNote}
              onAppendToNote={onAppendToNote}
            />
          )
        )}

        {/* Optimistic user message while sending */}
        {isSending && optimisticUserMessage && (
          <div className="flex justify-end">
            <div className="max-w-xl bg-primary text-white rounded-2xl rounded-tr-sm px-4 py-3 shadow-sm opacity-70">
              <p className="text-sm whitespace-pre-wrap">{optimisticUserMessage}</p>
            </div>
          </div>
        )}

        {/* Typing indicator */}
        {isSending && <TypingIndicator />}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <ChatInput
        value={inputValue}
        onChange={onInputChange}
        onSend={onSend}
        isLoading={isSending}
        remainingTokens={remainingTokens}
        tokensRefreshTime={tokensRefreshTime}
        onTokenLimitHit={onTokenLimitHit}
        conversationAtLimit={effectiveAtLimit}
      />
    </div>
  )
}

